import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { RadioCard } from '../../../../components/common/Radio/RadioCard';
import { IconButton } from '../../../../components/common/buttons/IconButton';
import { Icon } from '../../../../components/common/icon/Icon';
import { BasicInputWithExtras } from '../../../../components/common/inputs/BasicInputWithExtras';
import { SurveyForm } from '../../../../components/common/survey/form/SurveyForm';
import { SurveyInputContainer } from '../../../../components/common/survey/inputs/SurveyInputContainer';
import { SurveyInputHeader } from '../../../../components/common/survey/inputs/SurveyInputHeader';
import {
  SELL_TRACTOR_OPTION_VALUES,
  sellTractorSurveySteps,
  sellTractorSurveyWorkingMachineConstants,
} from '../../../../constants/survey/sellTractorSurvey';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store/useStore';
import { InputValue, SurveyFormOptionDetail, SurveyFormOptionType } from '../../../../interfaces/Element.interfaces';
import { userSellTractorSurveySlice } from '../../../../store/userSellTractorSurveySlice';
import { SellTractorSurveyPageWrapper } from '../SellTractorSurveyPageWrapper';

export const SellTractorSurveyWorkingMachinePage = () => {
  const navigate = useNavigate();

  const placeholderOfSecondInput = '예) 국제 EF트랙터용 태성 로더 프레임';
  const defaultPlaceholder = '입력하기';
  const idPrefix = 'working-';
  const INITIAL_WORKING_MACHINE_DETAILS: SurveyFormOptionDetail[] = [
    { id: `${idPrefix}0`, value: '', placeholder: '예) 대동 로타리 HC195GM' },
    { id: `${idPrefix}1`, value: '', placeholder: placeholderOfSecondInput },
  ];
  const INITIAL_NEXT_WORKING_MACHINE_ID = 2;

  const [workingMachine, setWorkingMachine] = useState<string>();
  const [workingMachineDetails, setWorkingMachineDetails] = useState<SurveyFormOptionDetail[]>(
    INITIAL_WORKING_MACHINE_DETAILS,
  );
  const [nextWorkingMachineId, setNextWorkingMachineId] = useState(INITIAL_NEXT_WORKING_MACHINE_ID);
  const [isWorkingMachineInputVisible, setIsWorkingMachineInputVisible] = useState(false);

  const dispatch = useAppDispatch();
  const userSellTractorSurveySelector = useAppSelector((state) => state.userSellTractorSurvey);
  const userSellTractorSurveyActions = userSellTractorSurveySlice.actions;

  const currentStep = sellTractorSurveySteps.STEP3;
  const steps = Object.keys(sellTractorSurveySteps);

  const handleAddInput = () => {
    if (workingMachineDetails.length >= 10) {
      return;
    }
    const placeholderText = workingMachineDetails.length === 1 ? placeholderOfSecondInput : defaultPlaceholder;
    const nextWorkingMachineDetails: SurveyFormOptionDetail[] = workingMachineDetails.concat({
      id: `${idPrefix}${nextWorkingMachineId}`,
      value: '',
      placeholder: placeholderText,
    });
    setWorkingMachineDetails(nextWorkingMachineDetails);
    setNextWorkingMachineId(nextWorkingMachineId + 1);
  };

  const handleRemoveInput = (id: string) => {
    const uniqueId = setWorkingMachineDetails((prevDetails) => prevDetails.filter((value) => value.id !== id));
  };

  const handleInputChange = (id: string, value: string) => {
    setWorkingMachineDetails((prevDetails) => {
      return prevDetails.map((detail) => (detail.id === id ? { ...detail, value } : detail));
    });
  };

  useEffect(() => {
    const { workingMachine, workingMachineDetails } = userSellTractorSurveySelector;
    setWorkingMachine(workingMachine);

    if (workingMachine !== SELL_TRACTOR_OPTION_VALUES.YES) {
      return;
    }

    setIsWorkingMachineInputVisible(true);

    if (workingMachineDetails && workingMachineDetails.length > 0) {
      let workingMachineIdSuffix = nextWorkingMachineId;
      const currentWorkingMachineDetails: SurveyFormOptionDetail[] = workingMachineDetails.map(
        (detail: string, index) => {
          switch (index) {
            case 0:
              return {
                ...INITIAL_WORKING_MACHINE_DETAILS[0],
                value: detail,
              };
            case 1:
              return {
                ...INITIAL_WORKING_MACHINE_DETAILS[1],
                value: detail,
              };
            default: {
              const convertedDetail: SurveyFormOptionDetail = {
                id: `${idPrefix}${workingMachineIdSuffix}`,
                value: detail,
                placeholder: defaultPlaceholder,
              };
              workingMachineIdSuffix += 1;
              return convertedDetail;
            }
          }
        },
      );
      setWorkingMachineDetails(currentWorkingMachineDetails);
      setNextWorkingMachineId(workingMachineIdSuffix);
    }
  }, [userSellTractorSurveySelector.option]);

  return (
    <>
      <SellTractorSurveyPageWrapper
        headerTitle={'작업기가 있나요?'}
        canConfirm
        confirmHandler={() => {
          if (workingMachine) {
            dispatch(userSellTractorSurveyActions.changeWorkingMachine(workingMachine));
          }

          if (
            workingMachine === SELL_TRACTOR_OPTION_VALUES.YES &&
            workingMachineDetails &&
            workingMachineDetails.length > 0
          ) {
            const convertedWorkingMachineDetails = workingMachineDetails
              .filter((detail) => !!detail.value)
              .map((value) => value.value);
            dispatch(userSellTractorSurveyActions.changeWorkingMachineDetails(convertedWorkingMachineDetails));
          }

          navigate('/survey/progress/sell-tractor/extra-info-1', { replace: true });
        }}
        prevButtonHandler={() => {
          navigate('/survey/progress/sell-tractor/option', { replace: true });
        }}
        steps={steps}
        currentStep={currentStep}
      >
        <SurveyForm padding={'0px 16px 24px 16px'}>
          <SurveyInputSection>
            <SurveyInputHeader inputTitle={'작업기 포함 유무'} isOptional />

            <SurveyInputContainer gridColumns={2}>
              {sellTractorSurveyWorkingMachineConstants.map(({ value, label }: SurveyFormOptionType) => (
                <RadioCard
                  key={value}
                  title={value}
                  label={label}
                  onClick={() => {
                    setWorkingMachine(value);

                    if (value === SELL_TRACTOR_OPTION_VALUES.YES) {
                      setIsWorkingMachineInputVisible(true);
                      return;
                    }
                    setIsWorkingMachineInputVisible(false);
                    setWorkingMachineDetails(INITIAL_WORKING_MACHINE_DETAILS);
                    setNextWorkingMachineId(INITIAL_NEXT_WORKING_MACHINE_ID);
                  }}
                  isActive={workingMachine === value}
                />
              ))}
            </SurveyInputContainer>
          </SurveyInputSection>

          {isWorkingMachineInputVisible && (
            <>
              <SurveyInputSection>
                {workingMachineDetails.map((detail) => {
                  if (detail.id === `${idPrefix}0`) {
                    return (
                      <BasicInputWithExtras
                        key={detail.id}
                        inputLabel={'옵션 내역'}
                        inputValue={detail.value}
                        placeholder={detail.placeholder}
                        onChange={(value: InputValue) => handleInputChange(detail.id, value as string)}
                      />
                    );
                  }
                  return (
                    <BasicInputWithExtras
                      key={detail.id}
                      inputValue={detail.value}
                      placeholder={detail.placeholder}
                      onChange={(value: InputValue) => handleInputChange(detail.id, value as string)}
                    >
                      <IconButton
                        onClickEvent={() => handleRemoveInput(detail.id)}
                        icon={{ name: 'MinusCircle', width: 20, height: 20 }}
                      />
                    </BasicInputWithExtras>
                  );
                })}
              </SurveyInputSection>
              <AddItemButton onClick={handleAddInput} disabled={workingMachineDetails.length >= 10}>
                <Icon name="Plus" width={14} height={14} />
                <AddItemButtonText>{'항목 추가'}</AddItemButtonText>
              </AddItemButton>
            </>
          )}
        </SurveyForm>
      </SellTractorSurveyPageWrapper>
    </>
  );
};

const SurveyInputSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const AddItemButton = styled.button`
  display: flex;
  max-height: 40px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
  &:disabled {
    opacity: 0.3;
  }
`;

const AddItemButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;
